import React from 'react';
import { Link } from 'react-router-dom';
import { usePostSubscription } from '../hooks/DataFetching/usePostSubscription';

const normalizeEligibility = (eligibleTo) => {
    const normalized = (eligibleTo || '').trim().toLowerCase();

    if (normalized === 'everyone' || normalized === 'open') {
        return 'Everyone';
    }

    if (normalized === 'application' || normalized === 'application required' || normalized === 'by application') {
        return 'Application';
    }

    if (normalized === 'invitation' || normalized === 'invitation only' || normalized === 'by invitation only') {
        return 'Invitation';
    }

    return eligibleTo || '';
};

export const PostSubscription = ({ groupId, eligibleTo, membership, onJoined, onLeft, isProfileManaged = false }) => {
    const { error, isLoading, postSubscription, deleteSubscription } = usePostSubscription();
    const normalizedEligibility = normalizeEligibility(eligibleTo);

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            const created = await postSubscription(groupId);
            if (onJoined) {
                onJoined(created);
            }
        } catch {
            // The hook exposes a user-facing error state.
        }
    };

    const handleLeave = async () => {
        if (!membership?.id) {
            return;
        }

        try {
            await deleteSubscription(membership.id);
            if (onLeft) {
                onLeft(membership);
            }
        } catch {
            // The hook exposes a user-facing error state.
        }
    };

    return (
        <div className="grid gap-3">
            {isProfileManaged && (
                <div className="rounded-lg border border-cyan-300/20 bg-cyan-400/10 p-3 text-sm text-cyan-100">
                    Geographic memberships are assigned from your profile address. Update your address in your profile or dashboard to change these memberships.
                </div>
            )}

            {membership && !isProfileManaged && (
                <div className="rounded-lg border border-emerald-300/30 bg-emerald-400/10 p-3 text-sm text-emerald-100">
                    <strong className="block text-emerald-50">You are a member.</strong>
                    <span className="mt-1 block">Open the member workspace to participate in forums, actions, coordination, and tasks.</span>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Link to={`/group-members/${groupId}`} className="tyk-action">
                            Open member workspace
                        </Link>
                        <button type="button" className="tyk-secondary-action" onClick={handleLeave} disabled={isLoading}>
                            {isLoading ? 'Leaving...' : 'Leave group'}
                        </button>
                    </div>
                </div>
            )}

            {!membership && !isProfileManaged && error && (
                <div role="alert" className="rounded-lg border border-amber-300/30 bg-amber-400/10 p-3 text-sm text-amber-100">
                    {error}
                </div>
            )}

            {!membership && !isProfileManaged && normalizedEligibility === 'Everyone' && (
                <div>
                    <p className="mb-3 text-sm text-slate-300">
                        Membership is open. Join to unlock the member workspace and group tools.
                    </p>
                    <form onSubmit={handleSubmit}>
                        <button type="submit" className="tyk-action" disabled={isLoading}>
                            {isLoading ? 'Joining...' : 'Join group'}
                        </button>
                    </form>
                </div>
            )}

            {!membership && !isProfileManaged && normalizedEligibility === 'Application' && (
                <div className="rounded-lg border border-cyan-300/20 bg-cyan-400/10 p-3 text-sm text-cyan-100">
                    This group requires an application. The application workflow is not available here yet.
                </div>
            )}

            {!membership && !isProfileManaged && normalizedEligibility === 'Invitation' && (
                <div className="rounded-lg border border-amber-300/20 bg-amber-400/10 p-3 text-sm text-amber-100">
                    This group is invitation only. Accepted invitations will appear in your membership list.
                </div>
            )}

            {!membership && !isProfileManaged && normalizedEligibility !== 'Everyone' && normalizedEligibility !== 'Application' && normalizedEligibility !== 'Invitation' && (
                <div className="rounded-lg border border-slate-300/20 bg-slate-900/50 p-3 text-sm text-slate-300">
                    Membership eligibility has not been published for this group.
                </div>
            )}
        </div>
    );
};
